import { useState } from 'react';

import { consoleDev, ConsoleStatus, fetchData } from '@quizzach/utils';
import { IPremadeQuiz } from '@quizzach/interfaces';

export const usePremadeQuizzes = () => {
	const [premadeQuizzes, setPremadeQuizzes] = useState<IPremadeQuiz[]>();

	const [premadeQuizzesById, setPremadeQuizzesById] = useState<IPremadeQuiz>(
		new IPremadeQuiz()
	);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<boolean>(false);
	const [premadeTags, setPremadeTags] = useState<string[]>([]);

	const getPremadeQuizzes = async (options?: {
		section?: string;
		author?: string;
		tag?: string;
	}) => {
		setLoading(true);

		try {
			// Build query parameters dynamically based on provided options
			const params = new URLSearchParams();
			if (options?.section) params.append('section', options.section);
			if (options?.author)
				params.append('author.display_name_slug', options.author);
			if (options?.tag)
				params.append('primary_thematic_tags.name', options.tag);

			const url = `/api/premade-quizzes?${params.toString()}`;

			const response = await fetchData<IPremadeQuiz[]>(url, {
				method: 'GET',
			});

			if (response.status >= 300) {
				consoleDev('fetchPremadeQuizzes', ConsoleStatus.HTTP, response.status);
				setError(true);
			} else {
				consoleDev('fetchPremadeQuizzes', ConsoleStatus.SUCC);
				setError(false);
				if (response.data) {
					setPremadeQuizzes(response.data);
				}
			}
		} catch (err) {
			consoleDev('fetchPremadeQuizzes', ConsoleStatus.NET, err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	const getPremadeQuizzesById = async (uuid: string) => {
		setLoading(true);
		setError(false);

		try {
			const response = await fetchData<IPremadeQuiz>(
				`/api/premade-quizzes/${uuid}`,
				{
					method: 'GET',
				}
			);
			if (response.status >= 300) {
				consoleDev(
					'getPremadeQuizzesById',
					ConsoleStatus.HTTP,
					response.status
				);
				setError(true);
			} else {
				consoleDev('getPremadeQuizzesById', ConsoleStatus.SUCC);
				if (response.data) {
					setPremadeQuizzesById(response.data);
				}
				setError(false);
			}
		} catch (err) {
			consoleDev('getPremadeQuizzesById', ConsoleStatus.NET, err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	const getPremadeTags = async () => {
		setLoading(true);

		try {
			const response = await fetchData<{ name: string }[]>(
				`/api/thematic-tags`,
				{
					method: 'GET',
				}
			);
			if (response.status >= 300) {
				consoleDev('getPremadeTags', ConsoleStatus.HTTP, response.status);
			} else {
				consoleDev('getPremadeTags', ConsoleStatus.SUCC);
				if (response.data) {
					const tags = response.data.map((item) => item.name);

					setPremadeTags(tags);
				}
			}
		} catch (err) {
			consoleDev('getPremadeTags', ConsoleStatus.NET, err);
		} finally {
			setLoading(false);
		}
	};

	return {
		premadeQuizzes,
		premadeTags,
		premadeQuizzesById,
		loading,
		error,
		getPremadeQuizzes,
		getPremadeQuizzesById,
		getPremadeTags,
	};
};
